import { Injectable } from '@angular/core';
import { ValidatorFn, Validators } from '@angular/forms';
import { DependentFieldFilters, Field } from '../interfaces/hubspot-form-data';
import { CustomGenericValidator } from '../validators/generic-validatiors.class';
import { CustomInputDateValidator } from '../validators/validate-date-input.class';
import { CustomInputEmailValidator } from '../validators/validate-email-input.class';
import { CustomInputNumberValidator } from '../validators/validate-number-input.class';
import { CustomInputPhoneValidator } from '../validators/validate-phone-input.class';
import { CustomSelectValidator } from '../validators/validate-select.class';
import { FormUtilsService } from './form-utils.service';

@Injectable()
export class ValidatorUtils {
    private _validatorByInputType = [
        { key: 'TEXT', func: ValidatorUtils.TEXTFIELD_VALIDATORS },
        { key: 'RADIO', func: ValidatorUtils.RADIO_VALIDATORS },
        { key: 'CHECKBOX', func: ValidatorUtils.CHECKBOX_VALIDATORS },
        { key: 'BOOLEANCHECKBOX', func: ValidatorUtils.BOOLEANCHECKBOX_VALIDATORS },
        { key: 'MODALCHECKBOX', func: ValidatorUtils.BOOLEANCHECKBOX_VALIDATORS },
        { key: 'TEXTAREA', func: ValidatorUtils.TEXTAREA_VALIDATORS },
        { key: 'DATE', func: ValidatorUtils.DATE_VALIDATORS },
        { key: 'SELECT', func: ValidatorUtils.SELECT_VALIDATORS },
        { key: 'NUMBER', func: ValidatorUtils.NUMBER_VALIDATORS },
        { key: 'EMAIL', func: ValidatorUtils.EMAIL_VALIDATORS },
        { key: 'PHONE', func: ValidatorUtils.PHONE_VALIDATORS },
        { key: 'FILE', func: ValidatorUtils.FILE_VALIDATORS }
    ];

    public static TEXTFIELD_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [CustomGenericValidator.validateTrimmed, CustomGenericValidator.maxLength];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static RADIO_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static CHECKBOX_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [];
        if (field.required) {
            _validators.push(Validators.requiredTrue);
        }
        return _validators;
    }

    public static BOOLEANCHECKBOX_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static TEXTAREA_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [CustomGenericValidator.validateTrimmed, CustomGenericValidator.maxLength];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static DATE_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [CustomInputDateValidator.validateDate];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static SELECT_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [];
        if (field.required) {
            _validators.push(CustomSelectValidator.validateRequiredSelect);
        }
        return _validators;
    }

    public static NUMBER_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [CustomInputNumberValidator.validateNumber(field), CustomInputNumberValidator.maxLength];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static EMAIL_VALIDATORS(field: Field, utilsService?: FormUtilsService): ValidatorFn[] {
        const _validators: ValidatorFn[] = [
            CustomInputEmailValidator.ValidateEmailAndCheckResubscribe(field, utilsService),
            CustomGenericValidator.maxLength
        ];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static PHONE_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = [CustomInputPhoneValidator.maxLength(field), CustomInputPhoneValidator.format(field)];
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    public static FILE_VALIDATORS(field: Field): ValidatorFn[] {
        const _validators: ValidatorFn[] = null;
        if (field.required) {
            _validators.push(Validators.required);
        }
        return _validators;
    }

    static setValidatorsAgain(field: DependentFieldFilters, _isVisible: boolean, _isRequired) {
        const _field = field;
        _field.dependentFormField.required = _isVisible && _isRequired;
        if (_field.dependentFormField.fieldType.toLowerCase() === 'radio') {
            return ValidatorUtils.RADIO_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'checkbox') {
            return ValidatorUtils.CHECKBOX_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'booleancheckbox') {
            return ValidatorUtils.BOOLEANCHECKBOX_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'textarea') {
            return ValidatorUtils.TEXTAREA_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'date') {
            return ValidatorUtils.DATE_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'file') {
            return ValidatorUtils.FILE_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'select') {
            return ValidatorUtils.SELECT_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'number') {
            return ValidatorUtils.NUMBER_VALIDATORS(_field.dependentFormField);
        } else if (_field.dependentFormField.fieldType.toLowerCase() === 'text') {
            if (_field.dependentFormField.name.toLowerCase() === 'email') {
                return ValidatorUtils.EMAIL_VALIDATORS(_field.dependentFormField);
            } else if (_field.dependentFormField.name.toLowerCase().indexOf('phone') > -1) {
                return ValidatorUtils.PHONE_VALIDATORS(_field.dependentFormField);
            } else {
                return ValidatorUtils.TEXTAREA_VALIDATORS(_field.dependentFormField);
            }
        } else {
            throw new Error('No function for fieldType ' + _field.dependentFormField.fieldType.toLowerCase());
        }
    }
}
